import { lazy, type ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { PlatformPermission } from '@/lib/permissions';
import { PlatformRoute } from '@/features/platform/guards';

/* Every /super-admin screen is its own chunk, guarded by a single platform permission — see
   features/platform/nav.ts for the matching sidebar entries. This is UX only; the API must independently
   verify the caller is platform staff on every request. */
const page = (loader: () => Promise<{ default: ComponentType }>) => lazy(loader);

const Dashboard = page(() => import('@/pages/platform/Dashboard'));
const Tenants = page(() => import('@/pages/platform/Tenants'));
const TenantDetail = page(() => import('@/pages/platform/TenantDetail'));
const TenantOnboarding = page(() => import('@/pages/platform/TenantOnboarding'));
const Plans = page(() => import('@/pages/platform/Plans'));
const Features = page(() => import('@/pages/platform/Features'));
const Subscriptions = page(() => import('@/pages/platform/Subscriptions'));
const Payments = page(() => import('@/pages/platform/Payments'));
const Analytics = page(() => import('@/pages/platform/Analytics'));
const AuditLog = page(() => import('@/pages/platform/AuditLog'));
const Settings = page(() => import('@/pages/platform/Settings'));

const g = (permission: PlatformPermission, Comp: ComponentType) => <PlatformRoute permission={permission}><Comp /></PlatformRoute>;

export const platformChildren: RouteObject[] = [
  { index: true, element: g('platform.dashboard.view', Dashboard) },
  { path: 'tenants', element: g('platform.tenants.read', Tenants) },
  { path: 'tenants/new', element: g('platform.tenants.manage', TenantOnboarding) },
  { path: 'tenants/:id', element: g('platform.tenants.read', TenantDetail) },
  { path: 'plans', element: g('platform.plans.manage', Plans) },
  { path: 'features', element: g('platform.features.manage', Features) },
  { path: 'subscriptions', element: g('platform.billing.manage', Subscriptions) },
  { path: 'payments', element: g('platform.billing.manage', Payments) },
  { path: 'analytics', element: g('platform.analytics.view', Analytics) },
  { path: 'audit-log', element: g('platform.audit.view', AuditLog) },
  { path: 'settings', element: g('platform.settings.manage', Settings) },
];
